import { isLeft, isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router';
import { DappDataProvider } from '../contexts/DappDataContext';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { useTRPC } from '../hooks/useTRPC';
import { hasDappStaffAccess } from '../utils/roleUtils';
import {
  getDappPathBySegment,
  replaceDappRouteSegment,
} from '../utils/dappRoutes';
import { PageTransition } from './PageTransition';

type DappRouteAccessScope = 'publisher' | 'admin';

export interface DappRouteContext {
  dappId: string;
  packageName: string | null;
  canonicalSegment: string;
  canonicalSegmentKind: 'package' | 'uuid-fallback';
  accessScope?: DappRouteAccessScope;
}

export function getDappRouteAccessScope(
  _pathname: string,
  hasStaffDappAccess: boolean,
): DappRouteAccessScope {
  return hasStaffDappAccess ? 'admin' : 'publisher';
}

export function getDappCanonicalRedirectPathname(
  pathname: string,
  canonicalSegment: string,
): string | null {
  const currentChildPath = pathname.match(/^\/dapp\/[^/]+(\/.*)?$/)?.[1] ?? '';
  const targetPathname =
    currentChildPath.length === 0 ||
    currentChildPath === '/' ||
    currentChildPath === '/home' ||
    currentChildPath === '/home/'
      ? getDappPathBySegment(canonicalSegment)
      : replaceDappRouteSegment(pathname, canonicalSegment);

  return targetPathname === pathname ? null : targetPathname;
}

export function DappLayout() {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const location = useLocation();
  const { dappKey } = useParams<{ dappKey: string }>();
  const [routeContext, setRouteContext] = useState<DappRouteContext | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [redirectPathname, setRedirectPathname] = useState<string | null>(null);
  const canUseStaffDappRoutes =
    authState.type === AuthStateType.Authenticated &&
    hasDappStaffAccess(authState.user.role, authState.user.email);

  useEffect(() => {
    let isCancelled = false;

    setRouteContext(null);
    setError(null);
    setRedirectPathname(null);

    if (!dappKey) {
      setError('No dapp route provided');
      return;
    }

    if (authState.type !== AuthStateType.Authenticated) {
      return;
    }

    const accessScope = getDappRouteAccessScope(
      location.pathname,
      canUseStaffDappRoutes,
    );

    void trpc.resolveDappRouteRef
      .query({ ref: dappKey, scope: accessScope })
      .then((result) => {
        if (isCancelled) {
          return;
        }

        if ('_tag' in result && isLeft(result)) {
          setError(result.left.message);
          return;
        }

        if (!('_tag' in result) || !isRight(result)) {
          setError('Dapp not found');
          return;
        }

        const resolved = result.right;
        const targetPathname = getDappCanonicalRedirectPathname(
          location.pathname,
          resolved.canonicalSegment,
        );

        if (targetPathname) {
          setRedirectPathname(targetPathname);
          return;
        }

        setRouteContext({
          dappId: resolved.id,
          packageName: resolved.packageName,
          canonicalSegment: resolved.canonicalSegment,
          canonicalSegmentKind: resolved.canonicalSegmentKind,
          accessScope,
        });
      })
      .catch(() => {
        if (!isCancelled) {
          setError('Dapp not found');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [authState.type, dappKey, canUseStaffDappRoutes, location.pathname, trpc]);

  if (!dappKey) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">No dapp route provided</p>
      </div>
    );
  }

  if (redirectPathname) {
    return (
      <Navigate
        to={{
          pathname: redirectPathname,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!routeContext) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-neutral-400">Loading dApp…</p>
      </div>
    );
  }

  const content = (
    <div className="h-full">
      <PageTransition>
        <Outlet context={routeContext} />
      </PageTransition>
    </div>
  );

  // Key prop ensures complete remount when switching between dapps.
  return (
    <DappDataProvider key={routeContext.dappId} dappId={routeContext.dappId}>
      {content}
    </DappDataProvider>
  );
}

export function useDappContext() {
  const context = useOutletContext<DappRouteContext | undefined>();
  if (!context) {
    throw new Error('useDappContext must be used under DappLayout');
  }
  return context;
}

export function useOptionalDappContext() {
  return useOutletContext<DappRouteContext | undefined>();
}
